import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable, interval, Subscription } from 'rxjs';
import { map, takeWhile } from 'rxjs/operators';

export interface PhoneOtpAttemptState {
  retryRemaining: number;
  resendRemaining: number;
  resendCountdown: number;
  isResendButtonDisabled: boolean;
  hasExceededMaxResends: boolean;
  hasExceededMaxRetries: boolean;
}

/**
 * Tracks OTP verification/resend attempts for the phone-number verification
 * flow in the users form. This is a copy of the authentication app's
 * OtpAttemptTrackerService, renamed to avoid a class clash, so the portal
 * libs (which cannot import from apps/authentication) can reuse the same
 * countdown / attempt-tracking behaviour.
 */
@Injectable({
  providedIn: 'root',
})
export class PhoneOtpAttemptTrackerService implements OnDestroy {
  private readonly MAX_RETRIES = 5;
  private readonly MAX_RESENDS = 5;
  private readonly SHORT_COOLDOWN_SECONDS = 30; // 30 seconds for attempts 1-4
  private readonly LONG_COOLDOWN_SECONDS = 600; // 10 minutes for attempt 5

  private state$ = new BehaviorSubject<PhoneOtpAttemptState>({
    retryRemaining: this.MAX_RETRIES,
    resendRemaining: this.MAX_RESENDS,
    resendCountdown: 0,
    isResendButtonDisabled: false,
    hasExceededMaxResends: false,
    hasExceededMaxRetries: false,
  });

  private countdownSubscription?: Subscription;

  get state(): Observable<PhoneOtpAttemptState> {
    return this.state$.asObservable();
  }

  get currentState(): PhoneOtpAttemptState {
    return this.state$.value;
  }

  ngOnDestroy(): void {
    this.stopCountdown();
  }

  /**
   * Initialize or reset the service state
   */
  reset(): void {
    this.stopCountdown();
    this.state$.next({
      retryRemaining: this.MAX_RETRIES,
      resendRemaining: this.MAX_RESENDS,
      resendCountdown: 0,
      isResendButtonDisabled: false,
      hasExceededMaxResends: false,
      hasExceededMaxRetries: false,
    });
  }

  /**
   * Update counts from server response
   */
  updateFromResponse(response: any): void {
    const currentState = this.state$.value;
    const updates: Partial<PhoneOtpAttemptState> = {};

    // Update retry count if present
    if (response?.retryStatus?.remaining_retries !== undefined) {
      updates.retryRemaining = Number(response.retryStatus.remaining_retries);
      updates.hasExceededMaxRetries = updates.retryRemaining === 0;
    }

    // Update resend count if present
    if (response?.resendStatus?.resend_remaining_count !== undefined) {
      const previousResendCount = currentState.resendRemaining;
      updates.resendRemaining = Number(response.resendStatus.resend_remaining_count);

      // Check if a resend was performed (count decreased)
      if (previousResendCount > updates.resendRemaining) {
        // Determine cooldown duration based on remaining resends
        if (updates.resendRemaining === 0) {
          // This was the 5th attempt
          updates.hasExceededMaxResends = true;
          this.startCountdown(this.LONG_COOLDOWN_SECONDS);
        } else {
          // Regular cooldown for attempts 1-4
          this.startCountdown(this.SHORT_COOLDOWN_SECONDS);
        }
      }
    }

    this.state$.next({ ...currentState, ...updates });
  }

  /**
   * Handle resend OTP action
   */
  handleResend(): void {
    const currentState = this.state$.value;

    // Decrease resend count
    const newResendRemaining = Math.max(0, currentState.resendRemaining - 1);

    // Determine cooldown duration
    let cooldownSeconds = this.SHORT_COOLDOWN_SECONDS;
    let hasExceededMaxResends = false;

    if (newResendRemaining === 0) {
      // This was the 5th attempt
      cooldownSeconds = this.LONG_COOLDOWN_SECONDS;
      hasExceededMaxResends = true;
    }

    // Start countdown
    this.startCountdown(cooldownSeconds);

    // Update state
    this.state$.next({
      ...currentState,
      resendRemaining: newResendRemaining,
      hasExceededMaxResends,
    });
  }

  /**
   * Start initial countdown without decrementing resend count
   * Used when OTP page is first shown
   */
  startInitialCountdown(): void {
    this.startCountdown(this.SHORT_COOLDOWN_SECONDS);
  }

  /**
   * Handle OTP verification attempt
   */
  handleVerifyAttempt(): void {
    const currentState = this.state$.value;

    // Decrease retry count
    const newRetryRemaining = Math.max(0, currentState.retryRemaining - 1);

    this.state$.next({
      ...currentState,
      retryRemaining: newRetryRemaining,
      hasExceededMaxRetries: newRetryRemaining === 0,
    });
  }

  /**
   * Reset OTP form to default state (when user clicks Request New OTP)
   */
  resetOtpForm(): void {
    const currentState = this.state$.value;

    // Reset retry count to max when new OTP is requested
    this.state$.next({
      ...currentState,
      retryRemaining: this.MAX_RETRIES,
      hasExceededMaxRetries: false,
    });
  }

  /**
   * Format countdown for display (MM:SS)
   */
  formatCountdown(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }

  /**
   * Start countdown timer
   */
  private startCountdown(seconds: number): void {
    // Stop any existing countdown
    this.stopCountdown();

    // Set initial countdown value
    const currentState = this.state$.value;
    this.state$.next({
      ...currentState,
      resendCountdown: seconds,
      isResendButtonDisabled: true,
    });

    // Start countdown interval
    this.countdownSubscription = interval(1000)
      .pipe(
        map((tick) => seconds - tick - 1),
        takeWhile((countdown) => countdown >= 0),
      )
      .subscribe({
        next: (countdown) => {
          const state = this.state$.value;
          this.state$.next({
            ...state,
            resendCountdown: countdown,
          });
        },
        complete: () => {
          // Countdown finished
          const state = this.state$.value;
          const updates: Partial<PhoneOtpAttemptState> = {
            resendCountdown: 0,
            isResendButtonDisabled: false,
          };

          // If we were in the 10-minute cooldown, reset the resend count
          if (state.hasExceededMaxResends) {
            updates.hasExceededMaxResends = false;
            updates.resendRemaining = this.MAX_RESENDS;
          }

          this.state$.next({ ...state, ...updates });
        },
      });
  }

  /**
   * Stop countdown timer
   */
  private stopCountdown(): void {
    if (this.countdownSubscription) {
      this.countdownSubscription.unsubscribe();
      this.countdownSubscription = undefined;
    }
  }
}
